import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SttFullDialog, SttTabs } from '../../../sporttia/all';
import GroupDetails from './GroupDetails';
import GroupMembers from './GroupMembers';
import GroupPrivileges from './GroupPrivileges';
import GroupFees from './GroupFees';
import GroupDevices from './GroupDevices';
import { useLoader, useQueryFilters } from '../../../lib/hooks';
import { Group as IGroup } from '../../../types/api/models';
import useGroupsService from '../../../services/GroupsService';
import SttError from '../../../components/error/SttError';
import { GetGroup200 } from '../../../types/api/paths/Group';
import constants from '../../../config/constants';
import { GROUP_MEMBERS } from '../../../lib/queryKeys';
import DateBuilder from '../../../lib/DateBuilder';
import type { Filters } from './utils/utils';

type GroupProps = {
    id?: number;
    trash?: boolean;
    onChange?: (group: IGroup) => void;
    onClose?: () => void;
    disableUrlPath?: boolean;
    initialTabKey?: string;
};

/**
 * Group screen
 */
export default function Group({
    id,
    trash = false,
    onChange,
    onClose,
    disableUrlPath,
    initialTabKey,
}: GroupProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [activeFeeTab, setActiveFeeTab] = useState(false);

    // Shared filters for both GroupMembers and GroupFees tabs
    const [filters, setFilters] = useQueryFilters<Filters>(
        {
            isActive: '0',
            upToDateWithPayments: '0',
            subscriptionType: 'all',
            subscriptionIni: new DateBuilder().ymd(),
            subscriptionEnd: new DateBuilder().ymd(),
            trash: false,
            name: '',
        },
        GROUP_MEMBERS,
    );

    const groupsService = useGroupsService();
    // Use 0 as fallback ID when id is null - queries won't run due to enabled check
    const effectiveId = id ?? 0;
    const getGroupQuery = groupsService.useGetGroup(
        effectiveId,
        { trash },
        {
            queryKey: ['groups', effectiveId, { trash }],
            enabled: !!id,
        },
    );
    const getGroupMembersQuery = groupsService.useGetGroupMembers(
        effectiveId,
        { rows: 25, page: 1 },
        {
            queryKey: ['groups', effectiveId, 'members', { rows: 25, page: 1 }],
            enabled: !!id,
        },
    );

    // Handle close - navigate back if opened from URL
    const handleClose = (): void => {
        if (onClose) {
            onClose();
        }
    };

    // Handle change
    const handleChange = (group: IGroup): void => {
        onChange?.(group);
    };

    useEffect(() => {
        if (getGroupMembersQuery.data) {
            if (getGroupMembersQuery.data.rows.some((m: any) => 'fees' in m)) {
                setActiveFeeTab(true);
            } else {
                setActiveFeeTab(false);
            }
        }
    }, [getGroupMembersQuery.data]);

    // Determine if fees tab should show
    const showFeesTab = useMemo(() => {
        if (!getGroupQuery.data?.group) return false;
        return (
            getGroupQuery.data.group.type !==
                constants.group.types.sport.name &&
            getGroupQuery.data.group.feePrice !== undefined &&
            getGroupQuery.data.group.feeInterval !== null
        );
    }, [getGroupQuery.data?.group]);

    // Define tabs configuration with keys for URL routing
    // Must be before early return to maintain hooks order
    const tabsConfig = useMemo(
        () => [
            { key: 'detail', show: true },
            { key: 'members', show: true },
            { key: 'fees', show: showFeesTab },
            { key: 'privileges', show: true },
            { key: 'devices', show: true },
        ],
        [showFeesTab],
    );

    // Get only visible tab keys
    const visibleTabKeys = useMemo(
        () => tabsConfig.filter((tab) => tab.show).map((tab) => tab.key),
        [tabsConfig],
    );

    // Calculate initial tab index from key
    const computedInitialTab = useMemo(() => {
        if (initialTabKey) {
            const index = visibleTabKeys.indexOf(initialTabKey);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [initialTabKey, visibleTabKeys]);

    const [isLoading, loader] = useLoader([
        getGroupQuery.status,
        getGroupMembersQuery.status,
    ]);

    // If no id, return null
    if (!id) {
        return null;
    }

    if (isLoading) {
        return loader;
    }

    if (getGroupQuery.isError || getGroupMembersQuery.isError) {
        return (
            <SttFullDialog open onClose={handleClose}>
                <SttError />
            </SttFullDialog>
        );
    }

    if (
        !getGroupQuery.isSuccess ||
        !getGroupMembersQuery.isSuccess ||
        !getGroupQuery.data?.group
    ) {
        return null;
    }

    // Handle tab change - update URL
    const handleTabChange = (tabIndex: number) => {
        const tabKey = visibleTabKeys[tabIndex];
        if (tabKey && id && !disableUrlPath) {
            navigate(`/groups/${id}/${tabKey}`, { replace: true });
        }
    };

    // Tabs configuration with components
    const tabs = [
        {
            key: 'detail',
            caption: t('generic.detail'),
            component: (
                <GroupDetails
                    item={getGroupQuery.data.group as any}
                    onSave={(savedGroup: any) => {
                        queryClient.setQueryData<GetGroup200>(
                            getGroupQuery.queryKey,
                            () => ({
                                group: savedGroup,
                            }),
                        );
                        handleChange(savedGroup);
                    }}
                    onDelete={(deletedGroup: any) => {
                        handleChange(deletedGroup);
                        handleClose();
                    }}
                />
            ),
        },
        {
            key: 'members',
            caption: t('sc.group.members'),
            component: (
                <GroupMembers
                    item={getGroupQuery.data.group as any}
                    haveFees={activeFeeTab}
                    filters={filters}
                    onFilterChange={setFilters}
                />
            ),
        },
        {
            key: 'fees',
            caption: t('generic.fees'),
            component: (
                <GroupFees
                    item={getGroupQuery.data.group as any}
                    filters={filters}
                    onFilterChange={setFilters}
                />
            ),
            show: showFeesTab,
        },
        {
            key: 'privileges',
            caption: t('generic.privileges'),
            component: (
                <GroupPrivileges item={getGroupQuery.data.group as any} />
            ),
        },
        {
            key: 'devices',
            caption: t('Devices'),
            component: <GroupDevices item={getGroupQuery.data.group as any} />,
        },
    ];

    return (
        <SttFullDialog
            open
            title={getGroupQuery.data.group.name}
            onClose={handleClose}
            urlPath={disableUrlPath || !id ? undefined : `/groups/${id}`}
        >
            <SttTabs
                tabs={tabs}
                initialTab={computedInitialTab}
                onTabChange={handleTabChange}
            />
        </SttFullDialog>
    );
}
